import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { RiCloseFill } from "react-icons/ri";
import { useField } from "remix-validated-form";

import { FieldError, FieldInfo } from "~/components/ui/forms/field-messages";
import { Label } from "~/components/ui/forms/label";
import { cn } from "~/utils/classnames";

interface FileInputProps extends ComponentPropsWithoutRef<"input"> {
  label: ReactNode;
  maxSize: string;
  multiple?: boolean;
  infoMessage?: ReactNode;
  errorMessage?: ReactNode;
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(function FileInput(
  { id, label, maxSize, multiple, infoMessage, errorMessage, onChange, className, ...props },
  ref
) {
  const genId = useId();
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

  const handleFileDelete = () => {
    setSelectedFiles(null);
    if (innerRef.current) {
      innerRef.current.value = "";
    }
  };

  return (
    <div className={cn("group", className)} aria-disabled={props.disabled}>
      <Label htmlFor={id || genId} required={props.required}>
        {label}
      </Label>
      <div className={cn("relative flex", label && "mt-1.5")}>
        <input
          id={id || genId}
          type="file"
          multiple={multiple}
          aria-invalid={!!errorMessage}
          aria-describedby={`${id || genId}-info ${id || genId}-error`}
          onChange={(e) => {
            setSelectedFiles(e.target.files);
            if (onChange) {
              onChange(e);
            }
          }}
          onClick={(e) => {
            e.currentTarget.value = "";
          }}
          className="invisible absolute size-0"
          ref={innerRef}
          {...props}
        />
        <label
          htmlFor={id || genId}
          className={cn(
            "flex h-8 cursor-pointer items-center justify-center rounded-full border border-transparent bg-grey-900 px-4 text-sm font-medium text-white hover:bg-grey-800 active:scale-[98%]",
            selectedFiles && "rounded-l-full rounded-r-none",
            "group-aria-disabled:opacity-60"
          )}
        >
          Sélectionner {multiple ? "vos" : "votre"} {label?.toString()?.toLowerCase()}
        </label>
        {selectedFiles ? (
          <div className="flex h-8 items-center justify-start gap-4 rounded-l-none rounded-r-full bg-grey-300 px-5 text-sm font-medium text-grey-900">
            {selectedFiles && selectedFiles[0] ? (
              <p className="leading-none">
                {`${
                  selectedFiles[0].name.length > 20
                    ? selectedFiles[0].name.substring(0, 20).concat("...")
                    : selectedFiles[0].name
                }${selectedFiles.length > 1 ? ` + ${selectedFiles.length - 1}` : ""}`}
              </p>
            ) : null}
            <button
              className="flex h-full items-center justify-center p-1 text-grey-900 hover:text-danger-500"
              onClick={handleFileDelete}
            >
              <RiCloseFill className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>
      <FieldInfo inputId={id || genId}>
        {multiple ? "Les" : "Le"} fichier{multiple ? "s" : ""} ne {multiple ? "doivent" : "doit"}{" "}
        pas dépasser {maxSize}.
      </FieldInfo>
      {multiple ? (
        <FieldInfo inputId={id || genId}>
          Pour insérer plusieurs fichiers, insérez-les en une seule fois.
        </FieldInfo>
      ) : null}
      {errorMessage ? <FieldError inputId={id || genId}>{errorMessage}</FieldError> : null}
    </div>
  );
});

export const ValidatedFileInput = forwardRef<HTMLInputElement, FileInputProps>(
  function ValidatedFileInput(props, ref) {
    const { error, getInputProps } = useField(props.name ?? "");

    return <FileInput ref={ref} {...props} {...getInputProps({ errorMessage: error, ...props })} />;
  }
);
